"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Timer, AlertTriangle, Check } from "lucide-react";

type Status = "fresh" | "moving" | "late";

const STATUS_COLOR: Record<Status, { bg: string; border: string; accent: string; tag: string }> = {
  fresh: {
    bg: "rgba(46, 204, 113, 0.10)",
    border: "rgba(46, 204, 113, 0.40)",
    accent: "#2ECC71",
    tag: "Fresh",
  },
  moving: {
    bg: "rgba(244, 168, 31, 0.12)",
    border: "rgba(244, 168, 31, 0.45)",
    accent: "#F4A81F",
    tag: "Moving",
  },
  late: {
    bg: "rgba(215, 38, 61, 0.12)",
    border: "rgba(215, 38, 61, 0.50)",
    accent: "#D7263D",
    tag: "Late",
  },
};

type TicketSpec = {
  num: string;
  time: string;
  status: Status;
  items: { en: string; es: string }[];
};

const TICKETS: TicketSpec[] = [
  {
    num: "#148",
    time: "1:08",
    status: "fresh",
    items: [
      { en: "1× Birria Plate", es: "1× Plato Birria" },
      { en: "2× Quesabirria", es: "2× Quesabirria" },
    ],
  },
  {
    num: "#147",
    time: "2:42",
    status: "moving",
    items: [
      { en: "1× Carne Asada", es: "1× Carne Asada" },
      { en: "1× Yuca Fries", es: "1× Yuca Fries" },
      { en: "1× Agua Fresca", es: "1× Agua Fresca" },
    ],
  },
  {
    num: "#145",
    time: "5:32",
    status: "late",
    items: [
      { en: "2× Pollo Bowl", es: "2× Bowl de Pollo" },
      { en: "1× Side Rice", es: "1× Arroz" },
    ],
  },
];

export default function KitchenDisplayUrgency() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Color is time", "El color es tiempo")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Green is fresh. ", "Verde es fresco. ")}
            <span className="text-redy-charcoal/30">
              {t("Red is now.", "Rojo es ya.")}
            </span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Every ticket carries its own timer. The card colors with age. Cooks don't have to read minutes — they read the room.",
              "Cada ticket trae su timer. La tarjeta cambia de color con la edad. Los cocineros no tienen que leer minutos — leen el ambiente."
            )}
          </p>
        </motion.div>

        {/* 3-ticket urgency row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-[1040px] mx-auto">
          {TICKETS.map((ticket, i) => (
            <motion.div
              key={ticket.num}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="bg-redy-charcoal rounded-[20px] p-5 shadow-[0_20px_50px_rgba(0,0,0,0.18)]"
            >
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
                <div className="text-[10px] font-black uppercase tracking-widest text-white/40">
                  KDS · {t("Grill", "Grill")}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#F4722B" }} />
                  <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#F4722B" }}>
                    Live
                  </span>
                </div>
              </div>

              <motion.div
                initial={{ scale: 0.97 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: 0.2 + 0.1 * i }}
                className="rounded-[12px] p-4 border"
                style={{
                  backgroundColor: STATUS_COLOR[ticket.status].bg,
                  borderColor: STATUS_COLOR[ticket.status].border,
                }}
              >
                <div className="flex justify-between items-center mb-3">
                  <div className="text-[18px] font-black text-white tabular-nums">{ticket.num}</div>
                  <div
                    className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded text-white"
                    style={{ backgroundColor: STATUS_COLOR[ticket.status].accent }}
                  >
                    {t(STATUS_COLOR[ticket.status].tag, statusEs(ticket.status))}
                  </div>
                </div>

                <div className="space-y-1 text-[12px] text-white/85 mb-4">
                  {ticket.items.map((item, j) => (
                    <div key={j}>{t(item.en, item.es)}</div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-white/10">
                  <div className="flex items-center gap-1.5 text-white/70">
                    <Timer className="w-3 h-3" />
                    <span className="text-[14px] font-black tabular-nums" style={{ color: STATUS_COLOR[ticket.status].accent }}>
                      {ticket.time}
                    </span>
                  </div>
                  <div className="text-[9px] font-bold uppercase tracking-wider text-white/40">
                    {t(STATUS_COLOR[ticket.status].tag, statusEs(ticket.status))}
                  </div>
                </div>
              </motion.div>

              <div className="mt-4 px-1 text-center">
                <div className="text-[11px] font-bold uppercase tracking-wider text-redy-charcoal/0">
                  &nbsp;
                </div>
                <div className="text-[13px] font-black text-white">
                  {t(captionEn(ticket.status), captionEs(ticket.status))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Allergen callout */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-20 max-w-[860px] mx-auto"
        >
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-center bg-white rounded-[28px] p-8 md:p-10 border border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.04)]">
            <div>
              <div className="text-[11px] font-black tracking-widest uppercase mb-3" style={{ color: "#D7263D" }}>
                {t("Notes · Top of the card", "Notas · Arriba de la tarjeta")}
              </div>
              <h3 className="text-2xl md:text-[36px] font-black tracking-tight text-redy-charcoal leading-tight mb-3">
                {t("No one buries", "Nadie esconde")}
                <br />
                <span className="text-redy-charcoal/30">
                  {t("an allergy.", "una alergia.")}
                </span>
              </h3>
              <p className="text-[14px] font-medium text-redy-secondary leading-relaxed">
                {t(
                  "When the order carries a note at the POS — like a nut allergy — the KDS ticket shows it at the top, where a tired cook at midnight can see it.",
                  "Cuando la orden lleva una nota en el POS — como una alergia a la nuez — el ticket del KDS la muestra arriba, donde un cocinero cansado a medianoche la puede ver."
                )}
              </p>
            </div>

            {/* Allergen ticket mockup */}
            <div className="w-full md:w-[260px] bg-redy-charcoal rounded-[16px] p-3.5">
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/10">
                <div className="text-[9px] font-black uppercase tracking-widest text-white/40">
                  KDS · {t("Expo", "Expo")}
                </div>
                <span className="text-[9px] font-bold tabular-nums text-white/60">0:42</span>
              </div>

              {/* Allergen banner */}
              <div
                className="rounded-[10px] px-3 py-2 mb-2 flex items-center gap-2"
                style={{ backgroundColor: "rgba(215, 38, 61, 0.18)", border: "1px solid rgba(215, 38, 61, 0.55)" }}
              >
                <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: "#D7263D" }} />
                <div className="text-[11px] font-black uppercase tracking-wider" style={{ color: "#FFFFFF" }}>
                  {t("Nut allergy · Table 7", "Alergia · Nuez · Mesa 7")}
                </div>
              </div>

              <div className="rounded-[10px] p-2.5" style={{ backgroundColor: "rgba(244, 114, 43, 0.08)", border: "1px solid rgba(244, 114, 43, 0.35)" }}>
                <div className="flex justify-between items-baseline mb-1.5">
                  <div className="text-[12px] font-black text-white">#149</div>
                  <div className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: "#F4722B" }}>
                    {t("Fire", "Cocinar")}
                  </div>
                </div>
                <div className="space-y-0.5 text-[10px] text-white/85">
                  <div>1× Pad Thai</div>
                  <div className="flex items-center gap-1 text-[9px]" style={{ color: "#D7263D" }}>
                    <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                    <span className="font-black uppercase">{t("NO peanuts", "SIN cacahuate")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function statusEs(status: Status) {
  if (status === "fresh") return "Fresco";
  if (status === "moving") return "Moviendo";
  return "Tarde";
}

function captionEn(status: Status) {
  if (status === "fresh") return "Just placed. Cooks have time.";
  if (status === "moving") return "Inside the window. Stay sharp.";
  return "Past the line. Act now.";
}

function captionEs(status: Status) {
  if (status === "fresh") return "Recién entró. Hay tiempo.";
  if (status === "moving") return "Dentro de la ventana. Atento.";
  return "Pasó la línea. Ya.";
}
